/** @jsx jsx */
import React, { forwardRef, type Ref } from 'react';
import { styled, css, jsx } from '@compiled/react';
import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled';
import { fg } from '@atlassian/jira-feature-gating';
import type { CommandRowProps } from '../../../types/commands/index.tsx';
import { OutlinedLozenge } from '../../custom-lozenge/index.tsx';
import {
	COMMAND_CONTAINER_TEST_ID,
	LEFT_ICON_TEST_ID,
	MODE_TEST_ID,
	COMMAND_ICON_CONTAINER_TEST_ID,
	COMMAND_INNER_CONTAINER_TEST_ID,
} from './constants.tsx';
import { RenderCommandName } from './render-command-name/index.tsx';
import { RightContent } from './right-content/index.tsx';

const activeStyles = css({
	color: token('color.text'),
	backgroundColor: token('color.background.selected'),
	position: 'relative',
	'&::before': {
		content: '""',
		position: 'absolute',
		top: 0,
		left: 0,
		bottom: 0,
		width: token('space.025'),
		backgroundColor: token('color.border.focused'),
	},
});

const leftIconAndNameContainerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	flex: 1,
	alignItems: 'center',
	overflow: 'hidden',
});

const outlinedLozengeStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: token('radius.small'),
	flexShrink: 0,
	height: '20px',
	width: '20px',
	font: token('font.code'),
	fontWeight: token('font.weight.semibold'),
	backgroundColor: token('color.background.input'),
	border: `1px solid ${token('color.border.bold')}`,
	color: token('color.text.subtlest'),
	boxSizing: 'border-box',
});

const styles = cssMap({
	root: {
		width: '100%',
	},
	commandContainer: {
		font: token('font.body'),
		color: token('color.text.subtle'),
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		marginInlineStart: token('space.negative.050'),
		marginInlineEnd: token('space.negative.050'),
	},
	innerContainer: {
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer',
		pointerEvents: 'auto',
	},
	iconsContainer: {
		marginRight: token('space.050'),
		gap: token('space.050'),
		display: 'flex',
		alignItems: 'center',
	},
	buttonWrapper: {
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
	},
	button: {
		width: '100%',
		padding: 0,
		backgroundColor: 'transparent',
		textAlign: 'left',
	},
});

export const CommandRow = forwardRef(
	(
		{ index, command, isActive, isFirstCommand, ...rest }: CommandRowProps,
		ref: Ref<HTMLDivElement>,
	) => {
		const InnerContainer = CommandContainerInner;

		if (!fg('dee-8683-compal-a11y-button-keyboard-accessibility')) {
			return (
				<CommandContainerWrapper data-testid={command.id}>
					<CommandContainer
						index={index}
						active={isActive}
						data-active={isActive}
						data-testid={COMMAND_CONTAINER_TEST_ID}
					>
						<InnerContainer data-testid={COMMAND_INNER_CONTAINER_TEST_ID} ref={ref} {...rest}>
							<CommandLeftIconAndNameContainer>
								<CommandIconsContainer data-testid={COMMAND_ICON_CONTAINER_TEST_ID}>
									{command.components?.LeftIcon && (
										<Flex
											testId={LEFT_ICON_TEST_ID}
											alignItems="center"
											justifyContent="center"
											xcss={[iconStyles, isActive && iconActiveStyles]}
										>
											<command.components.LeftIcon />
										</Flex>
									)}
									{command.mode && (
										<OutlinedLozenge data-testid={MODE_TEST_ID}>{command.mode}</OutlinedLozenge>
									)}
								</CommandIconsContainer>
								<RenderCommandName command={command} isActive={isActive} />
							</CommandLeftIconAndNameContainer>
							<RightContent command={command} isFirstCommand={isFirstCommand} isActive={isActive} />
						</InnerContainer>
					</CommandContainer>
				</CommandContainerWrapper>
			);
		}

		const content = (
			<div data-testid={command.id} css={styles.root}>
				<div
					data-active={isActive}
					data-testid={COMMAND_CONTAINER_TEST_ID}
					css={[styles.commandContainer, isActive && activeStyles]}
				>
					<div
						css={styles.innerContainer}
						data-testid={COMMAND_INNER_CONTAINER_TEST_ID}
						ref={ref}
						{...rest}
					>
						<div css={leftIconAndNameContainerStyles}>
							<div css={styles.iconsContainer} data-testid={COMMAND_ICON_CONTAINER_TEST_ID}>
								{command.components?.LeftIcon && (
									<Flex
										testId={LEFT_ICON_TEST_ID}
										alignItems="center"
										justifyContent="center"
										xcss={[iconStyles, isActive && iconActiveStyles]}
									>
										<command.components.LeftIcon />
									</Flex>
								)}
								{command.mode && (
									<div data-testid={MODE_TEST_ID} css={outlinedLozengeStyles}>
										{command.mode}
									</div>
								)}
							</div>
							<RenderCommandName command={command} isActive={isActive} />
						</div>
						<RightContent command={command} isFirstCommand={isFirstCommand} isActive={isActive} />
					</div>
				</div>
			</div>
		);

		if (command.components?.CommandRow) {
			return content;
		}

		return (
			<Pressable xcss={styles.button} aria-label={command.name}>
				{content}
			</Pressable>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandContainerWrapper = styled.div({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandContainer = styled.div<{ active?: boolean; index?: number }>(
	{
		font: token('font.body'),

		color: token('color.text.subtle'),
		paddingTop: token('space.050'),
		paddingRight: 0,
		paddingBottom: token('space.050'),
		paddingLeft: 0,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ active }) =>
		!!active &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			color: token('color.text'),

			backgroundColor: token('color.background.selected'),
			position: 'relative',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:before': {
				content: '""',
				position: 'absolute',
				top: 0,
				left: 0,
				bottom: 0,
				width: token('space.025'),

				backgroundColor: token('color.border.focused'),
			},
		}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandContainerInner = styled.div({
	paddingTop: token('space.050'),
	paddingRight: token('space.250'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.250'),
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	pointerEvents: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandIconsContainer = styled.div({
	marginRight: token('space.050'),
	gap: token('space.050'),
	display: 'flex',
	alignItems: 'center',
});

const iconStyles = xcss({
	width: '24px',
	height: '24px',
	// @ts-expect-error ts(2322) - this color should be set directly within icon after migration, but requires bigger change in types.
	color: 'color.icon.subtle',
});

const iconActiveStyles = xcss({
	// @ts-expect-error ts(2322) - this color should be set directly within icon after migration.
	color: 'color.icon',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommandLeftIconAndNameContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	flex: 1,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '24px',
	overflow: 'hidden',
});
